import type { ReactNode, Ref } from "react";
import { Link } from "react-router";

export interface Breadcrumb {
  label: string;
  to?: string;
}

export function PageHeader({
  breadcrumbs,
  title,
  titleAdornment,
  description,
  meta,
  actions,
  titleRef,
}: {
  breadcrumbs?: Breadcrumb[];
  title: string;
  /**
   * A handle on the heading, for the one thing a page cannot do without it:
   * MOVE FOCUS there after an in-app navigation (#1098).
   *
   * A route reached by a link from another surface otherwise leaves keyboard
   * focus on `body` — the control that was clicked is gone with the page it
   * was on — and a screen-reader user hears nothing about where they landed.
   * When a ref is supplied the heading also becomes programmatically
   * focusable (`tabIndex={-1}`); without one the markup is byte-for-byte
   * what every existing caller renders.
   */
  titleRef?: Ref<HTMLHeadingElement>;
  /**
   * A control belonging TO THE TITLE rather than to the page (#492).
   *
   * Added for the canonical library detail's Rename, which sits against the
   * heading it renames; put among `actions` it would read as an unrelated page
   * action. Optional and additive — every existing caller renders unchanged.
   */
  titleAdornment?: ReactNode;
  description?: ReactNode;
  /** Small line under the title, e.g. creation date. */
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      {/* `grow basis-80`, not a bare `min-w-0` (#908). With an `auto` basis this
          block is as wide as its longest line — a two-sentence description,
          typically — so `flex-wrap` pushed the action area onto its own row
          below it at every ordinary content width, and the reference puts the
          section's creation action at the TOP RIGHT of the heading row. A real
          20rem basis makes the title block shrink to share the row instead, and
          keeps the wrap for widths where the two genuinely do not fit. */}
      <div className="min-w-0 grow basis-80">
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <nav aria-label="Breadcrumb" className="mb-2">
            <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              {breadcrumbs.map((crumb, index) => (
                <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                  {index > 0 ? <span aria-hidden="true">/</span> : null}
                  {crumb.to ? (
                    <Link to={crumb.to} className="hover:text-foreground">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span aria-current="page">{crumb.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        ) : null}

        <h1
          {...(titleRef !== undefined ? { ref: titleRef, tabIndex: -1 } : {})}
          className="flex flex-wrap items-center gap-2 text-xl font-semibold tracking-tight text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {title}
          {titleAdornment}
        </h1>
        {/*
          Containers, not paragraphs: `description` and `meta` are ReactNode,
          and callers legitimately pass badges and loading skeletons. A <div>
          inside a <p> is invalid HTML, which the browser silently repairs by
          closing the paragraph early — the resulting tree no longer matches
          what React rendered.
        */}
        {description ? (
          <div className="mt-1 text-sm text-muted-foreground">{description}</div>
        ) : null}
        {meta ? (
          <div className="mt-1 text-sm text-muted-foreground">{meta}</div>
        ) : null}
      </div>

      {actions ? (
        // `max-w-full`: at 390px three actions are wider than the row, and
        // `shrink-0` alone kept them on one line past the viewport.
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
